const DEFAULT_UPDATE_CHECK_INTERVAL_MS = 60_000
const DEFAULT_REQUIRED_STABLE_CHECKS = 2
const DEFAULT_MAX_NAVIGATION_RELOAD_ATTEMPTS = 1
const APP_VERSION_PATH = '/app-version.json'
const RELOAD_ATTEMPT_STORAGE_PREFIX = 'app_update_reload_attempt:'
const RELOAD_ATTEMPT_COUNT_STORAGE_KEY = 'app_update_reload_attempt_count'

type UpdateCheckFetcher = typeof fetch
type IntervalSetter = (
    callback: () => void,
    intervalMs: number
) => ReturnType<typeof window.setInterval>
type IntervalClearer = (intervalId: ReturnType<typeof window.setInterval>) => void
type ReloadAttemptStorage = Pick<Storage, 'getItem' | 'setItem'>
type LocationAssigner = (targetUrl: string) => void

interface AppVersionManifest {
    fingerprint?: unknown
}

interface AppUpdateMonitorOptions {
    enabled?: boolean
    currentFingerprint?: string
    intervalMs?: number
    requiredStableChecks?: number
    maxNavigationReloadAttempts?: number
    fetcher?: UpdateCheckFetcher
    assignLocation?: LocationAssigner
    setInterval?: IntervalSetter
    clearInterval?: IntervalClearer
    storage?: ReloadAttemptStorage
}

interface ConsumePendingAppUpdateOptions {
    assignLocation?: LocationAssigner
    storage?: ReloadAttemptStorage
    maxNavigationReloadAttempts?: number
}

let updateCheckIntervalId: ReturnType<typeof window.setInterval> | null = null
let updateCheckInFlight = false
let activeClearInterval: IntervalClearer | null = null
let activeStorage: ReloadAttemptStorage | null = null
let activeAssignLocation: LocationAssigner | null = null
let activeMaxNavigationReloadAttempts = DEFAULT_MAX_NAVIGATION_RELOAD_ATTEMPTS
let pendingRemoteFingerprint = ''
let pendingRemoteFingerprintCount = 0
let pendingUpdateFingerprint = ''
const memoryReloadAttempts = new Map<string, string>()

const trimFingerprint = (fingerprint?: unknown): string =>
    typeof fingerprint === 'string' ? fingerprint.trim() : ''

const isProductionMode = (): boolean => import.meta.env.PROD

const buildUpdateCheckUrl = (): string =>
    `${APP_VERSION_PATH}?update-check=${Date.now()}`

const getDefaultStorage = (): ReloadAttemptStorage => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
        return window.sessionStorage
    }

    return {
        getItem: (key: string) => memoryReloadAttempts.get(key) ?? null,
        setItem: (key: string, value: string) => {
            memoryReloadAttempts.set(key, value)
        },
    }
}

const safeGetItem = (
    storage: ReloadAttemptStorage,
    key: string
): string | null => {
    try {
        return storage.getItem(key)
    } catch {
        return null
    }
}

const safeSetItem = (
    storage: ReloadAttemptStorage,
    key: string,
    value: string
): void => {
    try {
        storage.setItem(key, value)
    } catch {
        // Ignore storage failures so update checks never break page usage.
    }
}

const readCurrentAssetFingerprint = (): string => {
    if (typeof document === 'undefined') {
        return ''
    }

    const assetUrls = Array.from(
        document.querySelectorAll<HTMLLinkElement | HTMLScriptElement>(
            'script[src*="/assets/"], link[rel="stylesheet"][href*="/assets/"]'
        )
    )
        .map((element) => element.getAttribute('src') ?? element.getAttribute('href'))
        .filter((assetUrl): assetUrl is string => !!assetUrl)
        .map((assetUrl) => {
            try {
                return new URL(assetUrl, window.location.origin).pathname
            } catch {
                return assetUrl
            }
        })
        .sort()

    return assetUrls.join('|')
}

const readRemoteFingerprint = async (
    fetcher: UpdateCheckFetcher
): Promise<string> => {
    const response = await fetcher(buildUpdateCheckUrl(), {
        cache: 'no-store',
        credentials: 'same-origin',
    })

    if (!response.ok) {
        return ''
    }

    const manifest = (await response.json()) as AppVersionManifest

    return trimFingerprint(manifest.fingerprint)
}

const hasReloadAttempt = (
    storage: ReloadAttemptStorage,
    fingerprint: string
): boolean =>
    safeGetItem(storage, `${RELOAD_ATTEMPT_STORAGE_PREFIX}${fingerprint}`) ===
    '1'

const readReloadAttemptCount = (storage: ReloadAttemptStorage): number => {
    const rawValue = safeGetItem(storage, RELOAD_ATTEMPT_COUNT_STORAGE_KEY)
    const count = Number.parseInt(rawValue ?? '0', 10)

    return Number.isFinite(count) && count > 0 ? count : 0
}

const hasReachedReloadAttemptLimit = (
    storage: ReloadAttemptStorage,
    maxNavigationReloadAttempts: number
): boolean =>
    readReloadAttemptCount(storage) >= Math.max(0, maxNavigationReloadAttempts)

const markReloadAttempt = (
    storage: ReloadAttemptStorage,
    fingerprint: string
): void => {
    safeSetItem(
        storage,
        `${RELOAD_ATTEMPT_STORAGE_PREFIX}${fingerprint}`,
        '1'
    )
    safeSetItem(
        storage,
        RELOAD_ATTEMPT_COUNT_STORAGE_KEY,
        String(readReloadAttemptCount(storage) + 1)
    )
}

const updateStableFingerprintCounter = (remoteFingerprint: string): number => {
    if (remoteFingerprint !== pendingRemoteFingerprint) {
        pendingRemoteFingerprint = remoteFingerprint
        pendingRemoteFingerprintCount = 1
        return pendingRemoteFingerprintCount
    }

    pendingRemoteFingerprintCount += 1
    return pendingRemoteFingerprintCount
}

const checkForAppUpdate = async (
    currentFingerprint: string,
    fetcher: UpdateCheckFetcher,
    storage: ReloadAttemptStorage,
    requiredStableChecks: number,
    maxNavigationReloadAttempts: number
): Promise<void> => {
    if (updateCheckInFlight) {
        return
    }

    updateCheckInFlight = true

    try {
        const remoteFingerprint = await readRemoteFingerprint(fetcher)

        if (!remoteFingerprint || remoteFingerprint === currentFingerprint) {
            pendingRemoteFingerprint = ''
            pendingRemoteFingerprintCount = 0
            pendingUpdateFingerprint = ''
            return
        }

        const stableCheckCount =
            updateStableFingerprintCounter(remoteFingerprint)

        if (stableCheckCount < requiredStableChecks) {
            return
        }

        if (hasReloadAttempt(storage, remoteFingerprint)) {
            return
        }

        if (hasReachedReloadAttemptLimit(storage, maxNavigationReloadAttempts)) {
            return
        }

        pendingUpdateFingerprint = remoteFingerprint
    } catch (error) {
        console.debug('[app-update] Failed to check for app update.', error)
    } finally {
        updateCheckInFlight = false
    }
}

export const hasPendingAppUpdate = (): boolean => !!pendingUpdateFingerprint

export const consumePendingAppUpdateNavigation = (
    targetUrl: string,
    options: ConsumePendingAppUpdateOptions = {}
): boolean => {
    const fingerprint = pendingUpdateFingerprint

    if (!fingerprint) {
        return false
    }

    const storage = options.storage ?? activeStorage ?? getDefaultStorage()
    const maxNavigationReloadAttempts =
        options.maxNavigationReloadAttempts ??
        activeMaxNavigationReloadAttempts

    if (
        hasReloadAttempt(storage, fingerprint) ||
        hasReachedReloadAttemptLimit(storage, maxNavigationReloadAttempts)
    ) {
        pendingUpdateFingerprint = ''
        return false
    }

    markReloadAttempt(storage, fingerprint)
    pendingUpdateFingerprint = ''

    const assignLocation =
        options.assignLocation ??
        activeAssignLocation ??
        ((nextUrl: string) => window.location.assign(nextUrl))
    assignLocation(targetUrl)

    return true
}

export const getAppUpdateNavigationTarget = (
    event: MouseEvent
): string | null => {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    ) {
        return null
    }

    if (
        typeof window === 'undefined' ||
        !(event.target instanceof Element)
    ) {
        return null
    }

    const anchor = event.target.closest<HTMLAnchorElement>('a[href]')

    if (!anchor || anchor.hasAttribute('download')) {
        return null
    }

    const targetAttribute = anchor.getAttribute('target')

    if (targetAttribute && targetAttribute.toLowerCase() !== '_self') {
        return null
    }

    const targetUrl = new URL(anchor.href, window.location.href)
    const currentUrl = new URL(window.location.href)

    if (targetUrl.origin !== currentUrl.origin) {
        return null
    }

    if (
        targetUrl.pathname === currentUrl.pathname &&
        targetUrl.search === currentUrl.search
    ) {
        return null
    }

    return targetUrl.href
}

export const startAppUpdateMonitor = (
    options: AppUpdateMonitorOptions = {}
): void => {
    if (updateCheckIntervalId !== null) {
        return
    }

    const enabled = options.enabled ?? isProductionMode()
    const currentFingerprint = trimFingerprint(
        options.currentFingerprint !== undefined
            ? options.currentFingerprint
            : readCurrentAssetFingerprint()
    )

    if (!enabled || !currentFingerprint) {
        return
    }

    const fetcher = options.fetcher ?? window.fetch.bind(window)
    const storage = options.storage ?? getDefaultStorage()
    const requiredStableChecks = Math.max(
        1,
        options.requiredStableChecks ?? DEFAULT_REQUIRED_STABLE_CHECKS
    )
    const maxNavigationReloadAttempts = Math.max(
        0,
        options.maxNavigationReloadAttempts ??
            DEFAULT_MAX_NAVIGATION_RELOAD_ATTEMPTS
    )
    const setIntervalFn = options.setInterval ?? window.setInterval.bind(window)
    activeClearInterval =
        options.clearInterval ?? window.clearInterval.bind(window)
    activeStorage = storage
    activeAssignLocation =
        options.assignLocation ?? ((targetUrl) => window.location.assign(targetUrl))
    activeMaxNavigationReloadAttempts = maxNavigationReloadAttempts

    updateCheckIntervalId = setIntervalFn(() => {
        void checkForAppUpdate(
            currentFingerprint,
            fetcher,
            storage,
            requiredStableChecks,
            maxNavigationReloadAttempts
        )
    }, options.intervalMs ?? DEFAULT_UPDATE_CHECK_INTERVAL_MS)
}

export const stopAppUpdateMonitor = (clearIntervalFn?: IntervalClearer): void => {
    const clearInterval = clearIntervalFn ?? activeClearInterval

    if (updateCheckIntervalId !== null) {
        clearInterval?.(updateCheckIntervalId)
    }

    updateCheckIntervalId = null
    activeClearInterval = null
    activeStorage = null
    activeAssignLocation = null
    activeMaxNavigationReloadAttempts = DEFAULT_MAX_NAVIGATION_RELOAD_ATTEMPTS
    updateCheckInFlight = false
    pendingRemoteFingerprint = ''
    pendingRemoteFingerprintCount = 0
    pendingUpdateFingerprint = ''
    memoryReloadAttempts.clear()
}
